<template>
  <v-container class="mt-6">
    <v-row>
      <v-col cols="12">
        <h1 class="text-h5">Examples</h1>
        <p class="text-body-2 mb-0">
          Ready-to-run 2D simulations. Opening an example creates a copy in your account, so
          you can run it, change it and compare your results with the reference values.
        </p>
        <ol class="text-body-2 mt-2">
          <li><b>Open a copy</b> of an example below.</li>
          <li>In the simulator, press <b>Run Simulation</b> (Cell Properties panel).</li>
          <li>Open the results and <b>compare</b> them with the reference values on the card.</li>
        </ol>
      </v-col>
    </v-row>
    <v-row>
      <v-col v-for="example in examples" :key="example.id" cols="12" md="6">
        <v-card class="fill-height d-flex flex-column" outlined>
          <v-card-title class="pb-1 example-title">{{ example.title }}</v-card-title>
          <v-card-subtitle class="pb-2 mt-0 pt-1">
            <v-chip x-small class="mr-1">{{ example.category }}</v-chip>
            <v-chip x-small><v-icon x-small left>mdi-timer-outline</v-icon>{{ example.runtime }}</v-chip>
          </v-card-subtitle>
          <v-card-text class="flex-grow-1">
            <p>{{ example.description }}</p>
            <p class="text-caption mb-2"><strong>You will see:</strong> {{ example.learn.join(" · ") }}</p>
            <v-simple-table dense class="mb-2">
              <thead>
                <tr><th colspan="2">Reference results</th></tr>
              </thead>
              <tbody>
                <tr v-for="item in example.reference" :key="item.label">
                  <td class="text-caption">{{ item.label }}</td>
                  <td class="text-caption">{{ item.value }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <p class="text-caption mb-0">Source: {{ example.source }}</p>
            <v-expansion-panels v-if="example.steps" flat class="mt-2 example-steps">
              <v-expansion-panel>
                <v-expansion-panel-header class="px-0 text-body-2">
                  Build it yourself (step by step)
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <ol class="text-caption">
                    <li v-for="(step, index) in example.steps" :key="index" class="mb-1">{{ step }}</li>
                  </ol>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <p v-else class="text-caption mt-2 mb-0">
              With hundreds of rods, this one is meant to be opened as a copy and then edited
              (e.g. change the analyte material and see the resonance shift).
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              elevation="0"
              :loading="opening === example.id"
              :disabled="opening !== '' && opening !== example.id"
              @click="openExample(example)"
            >
              <v-icon left>mdi-content-copy</v-icon>
              Open a copy
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import api from "@/services/api";
import materials from "@/services/materials";
import { EXAMPLES, buildSimulationDocument } from "@/examples";

export default {
  name: "Examples",
  data: () => ({
    examples: EXAMPLES,
    opening: ""
  }),
  computed: {
    ...mapGetters("simulator", ["author"])
  },
  mounted() {
    document.title = "Examples - ElectrosFI";
  },
  methods: {
    async openExample(example) {
      this.opening = example.id;
      try {
        // Materiais prontos do backend: as geometrias do exemplo ficam ligadas a eles
        const builtin = await materials.get("/", { params: { author: this.author } })
          .then(({ data }) => (Array.isArray(data) ? data : []))
          .catch(() => []);
        const { data } = await api.post("/", buildSimulationDocument(example, this.author, builtin));
        if (data.err) throw new Error(data.err);
        this.$router.push("/simulator/" + data._id);
      } catch (err) {
        Swal.fire({ title: "Could not open the example", text: err.message });
      } finally {
        this.opening = "";
      }
    }
  }
};
</script>
<style scoped>
.example-title {
  line-height: 1.35;
}
.example-steps ::v-deep .v-expansion-panel {
  background: transparent !important;
}
.example-steps ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 0 8px;
}
</style>
